/**
 * useDueBannerItems — the data behind <UpcomingDueBanner>: payroll timecard
 * deadlines, admin-configured Deadline entries with their dashboard banner
 * turned on, and the logged-in user's own incomplete personal reminders.
 *
 * Pulled out of DashboardPage.js into a shared hook so DashboardLayout.js
 * (the persistent chrome around every authenticated route) can render the
 * banner globally -- "wherever they are in the app" -- instead of only on
 * the Dashboard page itself.
 */
import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';

export function mapApproverDueReminder(row, onEmailOptOut) {
  if (!row || !row.banner_text || !row.due_at) return null;
  const emailEnabled = row.email_enabled === true;
  return {
    key: row.key || `approver-${row.due_at}`,
    label: row.label || 'Timesheets to approve',
    bannerText: row.banner_text,
    dueAt: row.due_at,
    hoursBefore: row.hours_before ?? 24,
    emailOptOut: emailEnabled ? { optedOut: row.email_opt_out === true } : null,
    onEmailOptOut: emailEnabled && onEmailOptOut
      ? () => onEmailOptOut(row.email_opt_out === true)
      : undefined,
  };
}

export default function useDueBannerItems() {
  const [payrollDeadlines, setPayrollDeadlines] = useState([]);
  const [deadlineBanners, setDeadlineBanners] = useState([]);
  const [personalDueDates, setPersonalDueDates] = useState([]);
  // FMLA-derived reminders live on the case itself (not stored as personal
  // reminders). The serializer surfaces `requires_medical_proof` with a
  // context + message; we fold any truthy ones into the banner so the
  // dashboard says "You need to submit medical proof for case FMLA-…".
  // Clears automatically once the proof is uploaded (which clears the
  // case's requires_medical_proof on the next request).
  const [fmlaReminders, setFmlaReminders] = useState([]);
  const [approverReminders, setApproverReminders] = useState([]);

  const toggleApproverEmail = useCallback((optedOut) => {
    const next = !optedOut;
    api.patch('/attendance/timesheet-reminder-preference/', { email_opt_out: next })
      .then(() => {
        setApproverReminders((prev) => prev.map((row) => ({ ...row, email_opt_out: next })));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/attendance/calendar-entries/?page_size=500')
      .then((res) => {
        const entries = res.data?.results || res.data || [];
        setPayrollDeadlines(entries.filter((e) => e.event_type === 'payroll_deadline'));
        setDeadlineBanners(entries.filter((e) => e.event_type === 'deadline' && e.banner_enabled));
      })
      .catch(() => { setPayrollDeadlines([]); setDeadlineBanners([]); });

    api.get('/attendance/my-due-dates/?page_size=100')
      .then((res) => setPersonalDueDates(res.data?.results || res.data || []))
      .catch(() => setPersonalDueDates([]));

    // my-cases is permission-gated (attendance.leave). Users without the
    // permission get a 403; swallow and show nothing instead of logging.
    api.get('/attendance/fmla/cases/my-cases/')
      .then((res) => {
        const cases = res.data?.results || res.data || [];
        const items = [];
        for (const c of cases) {
          const r = c.requires_medical_proof;
          if (r && r.required) {
            items.push({
              key: `fmla-proof-${c.id}`,
              caseId: c.id,
              context: r.context,
              label: `${r.message} (${c.case_number})`,
            });
          }
        }
        setFmlaReminders(items);
      })
      .catch(() => setFmlaReminders([]));

    api.get('/attendance/approver-due-reminders/')
      .then((res) => setApproverReminders(res.data?.results || res.data || []))
      .catch(() => setApproverReminders([]));
  }, []);

  return [
    // e.date is a plain "YYYY-MM-DD" string -- append a local midnight time
    // so it parses as local midnight of the due date, not UTC midnight
    // (new Date('2026-06-30') parses as UTC, which shifts the day back for
    // any timezone west of UTC). A 24h window then reads naturally as
    // "shows all day on the day before it's due."
    ...payrollDeadlines.map((e) => ({
      key: `payroll-${e.id}`, label: e.label, dueAt: `${e.date}T00:00:00`, hoursBefore: 24,
    })),
    // Admin-configured Deadline entries -- only ones with the dashboard
    // banner explicitly turned on. bannerText/showCountdown come straight
    // from the admin's own configuration; hoursBefore uses the same
    // notify_threshold_hours that drives the real backend notification, so
    // the on-screen banner and the actual notification agree on timing.
    ...deadlineBanners.map((e) => ({
      key: `deadline-${e.id}`, label: e.label, bannerText: e.banner_text, showCountdown: e.banner_countdown_enabled,
      // due_time (if the admin set one) narrows "due sometime on this date"
      // down to an actual time, e.g. 5:00 PM instead of midnight.
      dueAt: `${e.date}T${e.due_time || '00:00:00'}`, hoursBefore: e.notify_threshold_hours || 24,
    })),
    ...personalDueDates
      .filter((d) => !d.completed)
      .map((d) => ({
        key: `personal-${d.id}`, label: d.title, dueAt: d.due_date, hoursBefore: d.reminder_hours_before,
      })),
    // FMLA reminders show immediately (no dueAt gate) because the
    // underlying requirement is open NOW, not scheduled for later. The
    // banner treats a missing dueAt as "always show" via a huge
    // hoursBefore so the reminder stays visible until proof is submitted.
    ...fmlaReminders.map((r) => ({
      key: r.key,
      label: r.label,
      dueAt: new Date().toISOString(),
      hoursBefore: 24 * 365,
    })),
    ...approverReminders
      .map((row) => mapApproverDueReminder(row, toggleApproverEmail))
      .filter(Boolean),
  ];
}
